import React from "react";
import {
  Box,
  Paper,
  Typography,
  Grid,
  Divider,
  Chip,
  Button,
  Alert,
} from "@mui/material";
import { format } from "date-fns";
import {
  ArrowBack as BackIcon,
  Warning as WarningIcon,
  CheckCircle as ApproveIcon,
} from "@mui/icons-material";
import { VENUES } from "@restaurant-staff-portal/shared";
import { usePermissions } from "@/hooks/usePermissions";
import { useBusinessConfig } from "@/hooks/useBusinessConfig";

// V2 Cash Out data structure from API
interface CashOutV2Data {
  id: string;
  venue: string;
  entryDate: Date | string;
  createdAt: Date | string;
  approved: boolean;
  approvedAt?: Date | string | null;

  // User relations
  user: {
    id: string;
    firstName: string;
    lastName: string;
  };
  approver?: {
    id: string;
    firstName: string;
    lastName: string;
  } | null;

  // Section 1: Toast Sales Data
  netSalesTotal: number | any;
  totalGratuityAndFees: number | any;
  taxes: number | any;
  giftCardSales: number | any;
  discounts: number | any;
  totalNonCashTips: number | any;

  // Section 2: Calculated (server-side)
  grossSales: number | any;
  calculatedNetSales: number | any;
  completeSales: number | any;

  // Section 3: Account Info
  staffAccountAmount: number | any;
  staffAccountTip: number | any;
  staffAccountGratuity: number | any;
  staffAccountTotal: number | any;
  houseAccountAmount: number | any;
  houseAccountTip: number | any;
  houseAccountGratuity: number | any;
  houseAccountTotal: number | any;

  // Section 4: Payment Centres
  cashAmount: number | any;
  cashTip: number | any;
  cashGratuity: number | any;
  cashTotal: number | any;
  creditAmount: number | any;
  creditTip: number | any;
  creditGratuity: number | any;
  creditTotal: number | any;
  giftCardAmount: number | any;
  giftCardTip: number | any;
  giftCardGratuity: number | any;
  giftCardTotal: number | any;

  // House Accounts (derived)
  houseAccountsAmount: number | any;
  houseAccountsTip: number | any;
  houseAccountsGratuity: number | any;
  houseAccountsTotal: number | any;

  // Payment Totals
  totalPaymentCentre: number | any;
  totalTipsPayment: number | any;

  // Section 5: Reconciliation
  salesReconciliation: number | any;
  tipsReconciliation: number | any;

  // Section 6: House Calculations
  trueSales: number | any;
  houseTips: number | any;

  // Section 7: Final
  owedToServerHouse: number | any;
}

interface CashOutDetailV2Props {
  cashOut: CashOutV2Data;
  onBack: () => void;
  onApprove?: (id: string) => void;
}

// Helper to convert Decimal to number
const decimalToNumber = (value: any): number => {
  if (typeof value === "object" && value !== null) {
    return Number(value.toString());
  }
  return Number(value);
};

export function CashOutDetailV2({
  cashOut,
  onBack,
  onApprove,
}: CashOutDetailV2Props) {
  const { cashOutPermissions } = usePermissions();
  const { houseTipPercentage } = useBusinessConfig();
  const canApprove = cashOutPermissions.canApprove;

  const formatCurrency = (amount: number | any) => {
    const numAmount =
      typeof amount === "number" ? amount : decimalToNumber(amount);
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(numAmount);
  };

  const getUserFullName = (user: { firstName: string; lastName: string }) => {
    return `${user.firstName} ${user.lastName}`.trim();
  };

  const getStatusColor = () => {
    if (cashOut.approved) return "success";
    return "warning";
  };

  const getStatusLabel = () => {
    if (cashOut.approved) return "Approved";
    return "Pending";
  };

  // Reconciliation status helpers
  const isBalanced = (value: number | any) => {
    return Math.abs(decimalToNumber(value)) < 0.01;
  };

  // Owed direction
  const owedAmount = decimalToNumber(cashOut.owedToServerHouse);
  const owedToHouse = owedAmount > 0;
  const owedLabel = owedToHouse ? "Owed to House" : "Owed to Server";

  return (
    <Box>
      <Paper sx={{ p: 3 }}>
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 3,
          }}
        >
          <Box>
            <Typography variant="h5">
              Cash Out Details -{" "}
              {VENUES[cashOut.venue as keyof typeof VENUES] || cashOut.venue}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Entry Date: {format(new Date(cashOut.entryDate), "MMMM dd, yyyy")}
            </Typography>
          </Box>
          <Chip label={getStatusLabel()} color={getStatusColor()} />
        </Box>

        {/* Basic Information */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" gutterBottom>
            Basic Information
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2" color="text.secondary">
                Server
              </Typography>
              <Typography variant="body1">
                {getUserFullName(cashOut.user)}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2" color="text.secondary">
                Created At
              </Typography>
              <Typography variant="body1">
                {format(new Date(cashOut.createdAt), "MMM dd, yyyy HH:mm")}
              </Typography>
            </Grid>
            {cashOut.approvedAt && cashOut.approver && (
              <>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="body2" color="text.secondary">
                    Approved By
                  </Typography>
                  <Typography variant="body1">
                    {getUserFullName(cashOut.approver)}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="body2" color="text.secondary">
                    Approved At
                  </Typography>
                  <Typography variant="body1">
                    {format(new Date(cashOut.approvedAt), "MMM dd, yyyy HH:mm")}
                  </Typography>
                </Grid>
              </>
            )}
          </Grid>
        </Box>

        {/* Section 1: Toast Sales Data */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" gutterBottom>
            Section 1: Toast Sales Data
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6} md={4}>
              <Typography variant="body2" color="text.secondary">
                Net Sales Total
              </Typography>
              <Typography variant="h6">
                {formatCurrency(cashOut.netSalesTotal)}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Typography variant="body2" color="text.secondary">
                Total Gratuity and Fees
              </Typography>
              <Typography variant="h6">
                {formatCurrency(cashOut.totalGratuityAndFees)}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Typography variant="body2" color="text.secondary">
                Taxes
              </Typography>
              <Typography variant="h6">
                {formatCurrency(cashOut.taxes)}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Typography variant="body2" color="text.secondary">
                Gift Card Sales
              </Typography>
              <Typography variant="h6">
                {formatCurrency(cashOut.giftCardSales)}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Typography variant="body2" color="text.secondary">
                Discounts
              </Typography>
              <Typography variant="h6">
                {formatCurrency(cashOut.discounts)}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Typography variant="body2" color="text.secondary">
                Total Non-Cash Tips
              </Typography>
              <Typography variant="h6">
                {formatCurrency(cashOut.totalNonCashTips)}
              </Typography>
            </Grid>
          </Grid>
        </Box>

        {/* Section 2: Calculated Sales Summary */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" gutterBottom>
            Section 2: Calculated Sales Summary
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <Typography variant="body2" color="text.secondary">
                Gross Sales
              </Typography>
              <Typography variant="h6" color="primary">
                {formatCurrency(cashOut.grossSales)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Net Sales + Gratuity/Fees + Taxes + Discounts
              </Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="body2" color="text.secondary">
                Calculated Net Sales
              </Typography>
              <Typography variant="h6" color="primary">
                {formatCurrency(cashOut.calculatedNetSales)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Gross Sales - Discounts
              </Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="body2" color="text.secondary">
                Complete Sales
              </Typography>
              <Typography variant="h6" color="primary">
                {formatCurrency(cashOut.completeSales)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Net Sales + Non-Cash Tips + Gift Card Sales
              </Typography>
            </Grid>
          </Grid>
        </Box>

        {/* Section 3: Account Info */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" gutterBottom>
            Section 3: Account Info
          </Typography>
          <Divider sx={{ mb: 2 }} />

          {/* Staff Account */}
          <Typography variant="subtitle2" sx={{ mb: 2 }}>
            Staff Account
          </Typography>
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={6} sm={3}>
              <Typography variant="body2" color="text.secondary">
                Amount
              </Typography>
              <Typography variant="h6">
                {formatCurrency(cashOut.staffAccountAmount)}
              </Typography>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Typography variant="body2" color="text.secondary">
                Tip
              </Typography>
              <Typography variant="h6">
                {formatCurrency(cashOut.staffAccountTip)}
              </Typography>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Typography variant="body2" color="text.secondary">
                Gratuity
              </Typography>
              <Typography variant="h6">
                {formatCurrency(cashOut.staffAccountGratuity)}
              </Typography>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Typography variant="body2" color="text.secondary">
                Total
              </Typography>
              <Typography variant="h6" color="primary">
                {formatCurrency(cashOut.staffAccountTotal)}
              </Typography>
            </Grid>
          </Grid>

          {/* House Account */}
          <Typography variant="subtitle2" sx={{ mb: 2 }}>
            House Account
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={6} sm={3}>
              <Typography variant="body2" color="text.secondary">
                Amount
              </Typography>
              <Typography variant="h6">
                {formatCurrency(cashOut.houseAccountAmount)}
              </Typography>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Typography variant="body2" color="text.secondary">
                Tip
              </Typography>
              <Typography variant="h6">
                {formatCurrency(cashOut.houseAccountTip)}
              </Typography>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Typography variant="body2" color="text.secondary">
                Gratuity
              </Typography>
              <Typography variant="h6">
                {formatCurrency(cashOut.houseAccountGratuity)}
              </Typography>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Typography variant="body2" color="text.secondary">
                Total
              </Typography>
              <Typography variant="h6" color="primary">
                {formatCurrency(cashOut.houseAccountTotal)}
              </Typography>
            </Grid>
          </Grid>
        </Box>

        {/* Section 4: Payment Centres */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" gutterBottom>
            Section 4: Payment Centres
          </Typography>
          <Divider sx={{ mb: 2 }} />

          {/* Cash */}
          <Typography variant="subtitle2" sx={{ mb: 2 }}>
            Cash (Amount + Tip + Gratuity)
          </Typography>
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={6} sm={3}>
              <Typography variant="body2" color="text.secondary">
                Amount
              </Typography>
              <Typography variant="h6">
                {formatCurrency(cashOut.cashAmount)}
              </Typography>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Typography variant="body2" color="text.secondary">
                Tip
              </Typography>
              <Typography variant="h6">
                {formatCurrency(cashOut.cashTip)}
              </Typography>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Typography variant="body2" color="text.secondary">
                Gratuity
              </Typography>
              <Typography variant="h6">
                {formatCurrency(cashOut.cashGratuity)}
              </Typography>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Typography variant="body2" color="text.secondary">
                Total
              </Typography>
              <Typography variant="h6" color="primary">
                {formatCurrency(cashOut.cashTotal)}
              </Typography>
            </Grid>
          </Grid>

          {/* Credit */}
          <Typography variant="subtitle2" sx={{ mb: 2 }}>
            Credit (Amount + Tip + Gratuity)
          </Typography>
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={6} sm={3}>
              <Typography variant="body2" color="text.secondary">
                Amount
              </Typography>
              <Typography variant="h6">
                {formatCurrency(cashOut.creditAmount)}
              </Typography>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Typography variant="body2" color="text.secondary">
                Tip
              </Typography>
              <Typography variant="h6">
                {formatCurrency(cashOut.creditTip)}
              </Typography>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Typography variant="body2" color="text.secondary">
                Gratuity
              </Typography>
              <Typography variant="h6">
                {formatCurrency(cashOut.creditGratuity)}
              </Typography>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Typography variant="body2" color="text.secondary">
                Total
              </Typography>
              <Typography variant="h6" color="primary">
                {formatCurrency(cashOut.creditTotal)}
              </Typography>
            </Grid>
          </Grid>

          {/* Gift Card */}
          <Typography variant="subtitle2" sx={{ mb: 2 }}>
            Gift Card (Amount + Tip + Gratuity)
          </Typography>
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={6} sm={3}>
              <Typography variant="body2" color="text.secondary">
                Amount
              </Typography>
              <Typography variant="h6">
                {formatCurrency(cashOut.giftCardAmount)}
              </Typography>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Typography variant="body2" color="text.secondary">
                Tip
              </Typography>
              <Typography variant="h6">
                {formatCurrency(cashOut.giftCardTip)}
              </Typography>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Typography variant="body2" color="text.secondary">
                Gratuity
              </Typography>
              <Typography variant="h6">
                {formatCurrency(cashOut.giftCardGratuity)}
              </Typography>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Typography variant="body2" color="text.secondary">
                Total
              </Typography>
              <Typography variant="h6" color="primary">
                {formatCurrency(cashOut.giftCardTotal)}
              </Typography>
            </Grid>
          </Grid>

          {/* House Accounts (Combined Staff + House) */}
          <Typography variant="subtitle2" sx={{ mb: 2 }}>
            House Accounts (Staff + House Combined)
          </Typography>
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={6} sm={3}>
              <Typography variant="body2" color="text.secondary">
                Amount
              </Typography>
              <Typography variant="h6">
                {formatCurrency(cashOut.houseAccountsAmount)}
              </Typography>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Typography variant="body2" color="text.secondary">
                Tip
              </Typography>
              <Typography variant="h6">
                {formatCurrency(cashOut.houseAccountsTip)}
              </Typography>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Typography variant="body2" color="text.secondary">
                Gratuity
              </Typography>
              <Typography variant="h6">
                {formatCurrency(cashOut.houseAccountsGratuity)}
              </Typography>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Typography variant="body2" color="text.secondary">
                Total
              </Typography>
              <Typography variant="h6" color="primary">
                {formatCurrency(cashOut.houseAccountsTotal)}
              </Typography>
            </Grid>
          </Grid>

          {/* Payment Totals */}
          <Divider sx={{ my: 2 }} />
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary">
                Total Payment Centre
              </Typography>
              <Typography variant="h6" color="primary">
                {formatCurrency(cashOut.totalPaymentCentre)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Cash + Credit + Gift Card + House Accounts
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary">
                Total Tips (Payment)
              </Typography>
              <Typography variant="h6" color="primary">
                {formatCurrency(cashOut.totalTipsPayment)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Sum of all tips from payment centres
              </Typography>
            </Grid>
          </Grid>
        </Box>

        {/* Section 5: Reconciliation */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" gutterBottom>
            Section 5: Reconciliation
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Alert
                severity={isBalanced(cashOut.salesReconciliation) ? "success" : "warning"}
                icon={!isBalanced(cashOut.salesReconciliation) ? <WarningIcon /> : undefined}
              >
                <Typography variant="subtitle2">
                  Sales Reconciliation
                </Typography>
                <Typography variant="h6">
                  {formatCurrency(cashOut.salesReconciliation)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Complete Sales ({formatCurrency(cashOut.completeSales)}) - Total Payment Centre ({formatCurrency(cashOut.totalPaymentCentre)})
                </Typography>
              </Alert>
            </Grid>
            <Grid item xs={12} md={6}>
              <Alert
                severity={isBalanced(cashOut.tipsReconciliation) ? "success" : "warning"}
                icon={!isBalanced(cashOut.tipsReconciliation) ? <WarningIcon /> : undefined}
              >
                <Typography variant="subtitle2">
                  Tips Reconciliation
                </Typography>
                <Typography variant="h6">
                  {formatCurrency(cashOut.tipsReconciliation)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Non-Cash Tips ({formatCurrency(cashOut.totalNonCashTips)}) - Total Tips Payment ({formatCurrency(cashOut.totalTipsPayment)})
                </Typography>
              </Alert>
            </Grid>
          </Grid>
        </Box>

        {/* Section 6: House Calculations */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" gutterBottom>
            Section 6: House Calculations
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                }}
              >
                <Box>
                  <Typography variant="body1" fontWeight="medium">
                    True Sales
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Calculated Net Sales - Staff Account Total - Total Gratuity and Fees
                  </Typography>
                </Box>
                <Typography variant="h6" fontWeight="bold">
                  {formatCurrency(cashOut.trueSales)}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                }}
              >
                <Box>
                  <Typography variant="body1" fontWeight="medium">
                    House Tips
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    True Sales × {(houseTipPercentage * 100).toFixed(1)}%
                  </Typography>
                </Box>
                <Typography variant="h6" fontWeight="bold">
                  {formatCurrency(cashOut.houseTips)}
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Box>

        {/* Section 7: Final Calculation - Owed to Server/House */}
        <Box sx={{ mt: 4 }}>
          <Typography variant="h6" gutterBottom>
            Section 7: Final Calculation
          </Typography>
          <Divider sx={{ mb: 2 }} />

          <Alert
            severity={owedToHouse ? "error" : "success"}
            sx={{
              "& .MuiAlert-message": {
                width: "100%",
              },
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                width: "100%",
              }}
            >
              <Box>
                <Typography variant="body1" fontWeight="medium">
                  {owedLabel}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  (-Non-Cash Tips + House Tips) + Cash Total - Total Gratuity and Fees
                </Typography>
              </Box>
              <Typography variant="h6" fontWeight="bold">
                {formatCurrency(Math.abs(owedAmount))}
              </Typography>
            </Box>
          </Alert>
        </Box>
      </Paper>

      {/* Action Buttons */}
      <Box
        sx={{ display: "flex", gap: 2, justifyContent: "space-between", mt: 3 }}
      >
        <Button startIcon={<BackIcon />} onClick={onBack} variant="outlined">
          Back to List
        </Button>

        {canApprove && onApprove && (
          <Button
            startIcon={<ApproveIcon />}
            onClick={cashOut.approved ? undefined : () => onApprove(cashOut.id)}
            variant="contained"
            color={cashOut.approved ? "inherit" : "success"}
            disabled={cashOut.approved}
          >
            {cashOut.approved ? "Already Approved" : "Approve Cash Out"}
          </Button>
        )}
      </Box>
    </Box>
  );
}
